import { ReadonlyVec3, vec3 } from 'gl-matrix';
import { dominantComponent } from '~/domain/geometry/patientGeometry';
import type { Environment } from '~/engine/data/Environment';
import type { ContourTexture } from '~/engine/data/structure/ContourTexture';
import type { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';

/** A structure's extent along one axis, as a line the displayed slice moves on. */
export interface SliceSweep {
    /** The structure's near face on the axis, through its centre, in patient mm. */
    start: vec3;
    /** The structure's far face on the axis, through its centre, in patient mm. */
    end: vec3;
    /** One slice width along the axis, the step a wheel tick advances, in mm. */
    stepMm: number;
}

/** The structure `roiNumber` of the Environment's structure set, if it holds it. */
export function structureOf(
    environment: Environment | undefined,
    roiNumber: number
): RegionOfInterest | undefined {
    return environment?.structureSet?.getRoi(roiNumber);
}

/**
 * The line through a structure's centre along `axis`, from the face of its
 * content bounding box nearest along the axis to the farthest, with the
 * image's slice width along that axis. `texture` is the structure's own,
 * chosen by the caller: `builtTexture` for a reader that must not build
 * one, `texture` for a user action that may. The box is scanned once.
 * Undefined when there is nothing to sweep: no structure, axis or texture,
 * an empty texture, or a structure that spans a single plane along the
 * axis.
 */
export function sliceSweepOf(
    roi: RegionOfInterest | undefined,
    axis: ReadonlyVec3 | undefined,
    texture: ContourTexture | undefined
): SliceSweep | undefined {
    if (!roi || !axis || !texture || texture.isEmpty) {
        return undefined;
    }
    const box = texture.contentBoundingBox;
    const { min, max } = box;
    if (!min || !max) {
        return undefined;
    }
    const center = box.center;
    const faceAlong = (corner: ReadonlyVec3): vec3 =>
        vec3.scaleAndAdd(
            vec3.create(),
            center,
            axis,
            vec3.dot(axis, vec3.subtract(vec3.create(), corner, center))
        );
    const start = faceAlong(min);
    const end = faceAlong(max);
    if (!(vec3.squaredDistance(start, end) > 0)) {
        return undefined;
    }
    return { start, end, stepMm: roi._imageGrid.voxelSpacingAlong(axis) };
}

/**
 * Whether a sweep through the structure can be asked for, answered without
 * building anything: there is an axis, and the structure is a contour
 * structure with content. `builtTexture` is the structure's resident
 * texture, if any: with one the extent along the axis is checked too; a
 * structure with content on a single plane whose texture is not yet built
 * is answered true here and refused when the sweep is asked for.
 */
export function canSweep(
    roi: RegionOfInterest | undefined,
    axis: ReadonlyVec3 | undefined,
    builtTexture: ContourTexture | undefined
): boolean {
    if (!axis || !roi || roi.isPointRoi || roi.isEmpty) {
        return false;
    }
    return (
        builtTexture === undefined ||
        sliceSweepOf(roi, axis, builtTexture) !== undefined
    );
}

/**
 * `axis` or its opposite, whichever points along the positive patient
 * direction of its dominant component, so a scalar along it runs low to high
 * in patient coordinates whichever way a view looks along the line.
 */
export function towardPositive(axis: ReadonlyVec3): vec3 {
    return dominantComponent(axis).sign < 0
        ? vec3.negate(vec3.create(), axis)
        : vec3.clone(axis);
}
